import { FastshiftTicketStatus } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';

import { ControlsPayload } from '@/common/fastshift-table';
import { getTicketStatusText } from '@/common/utils/ticket-status';

import { useMigrationTicketsUIAnalytics } from '../hooks';
import { FILTER_KEYS, FilterOption, MigrationPhase } from '../types';
import { getMigrationPhaseDisplayText } from '../utils';

import { messages } from './messages';
import { MigrationPhaseFilterDropdown } from './migration-phase-filter-dropdown/migration-phase-filter-dropdown';
import { StatusFilterDropdown } from './status-filter-dropdown/status-filter-dropdown';

type MigrationTicketsFiltersProps = {
	filters: Record<string, string | null>;
	onSetControlsPayload: (controls: ControlsPayload) => void;
	allControls: ControlsPayload;
};

export const MigrationTicketsFilters = ({
	filters,
	onSetControlsPayload,
	allControls,
}: MigrationTicketsFiltersProps) => {
	const { formatMessage } = useIntl();
	const {
		sendStatusFilterDropdownClickUIEvent,
		sendStatusFilterDropdownItemClickUIEvent,
		sendMigrationPhaseDropdownClickUIEvent,
		sendMigrationPhaseDropdownItemClickUIEvent,
		sendClearFiltersButtonClickUIEvent,
	} = useMigrationTicketsUIAnalytics();

	const handleFilterChange = (key: string, option: FilterOption<string> | null) => {
		const newFilters = { ...filters };

		if (option === null) {
			delete newFilters[key];
		} else {
			newFilters[key] = option.value;
		}

		onSetControlsPayload({
			...allControls,
			filters: Object.keys(newFilters).length > 0 ? newFilters : {},
		});
	};

	const selectedStatus = filters?.[FILTER_KEYS.STATUS]
		? ({
				label: formatMessage(
					getTicketStatusText(filters[FILTER_KEYS.STATUS] as FastshiftTicketStatus),
				),
				value: filters[FILTER_KEYS.STATUS],
			} as FilterOption<FastshiftTicketStatus>)
		: null;

	const selectedPhase = filters?.[FILTER_KEYS.MIGRATION_PHASE]
		? ({
				label: formatMessage(
					getMigrationPhaseDisplayText(filters[FILTER_KEYS.MIGRATION_PHASE] as MigrationPhase),
				),
				value: filters[FILTER_KEYS.MIGRATION_PHASE],
			} as FilterOption<MigrationPhase>)
		: null;

	const hasActiveFilters = filters && Object.keys(filters).length > 0;

	const handleClearFilters = () => {
		onSetControlsPayload({
			...allControls,
			filters: {},
		});
	};

	return (
		<>
			<StatusFilterDropdown
				selectedStatus={selectedStatus}
				onStatusChange={(option) => handleFilterChange(FILTER_KEYS.STATUS, option)}
				sendStatusFilterDropdownClickUIEvent={sendStatusFilterDropdownClickUIEvent}
				sendStatusFilterDropdownItemClickUIEvent={sendStatusFilterDropdownItemClickUIEvent}
			/>

			<MigrationPhaseFilterDropdown
				selectedPhase={selectedPhase}
				onPhaseChange={(option) => handleFilterChange(FILTER_KEYS.MIGRATION_PHASE, option)}
				sendMigrationPhaseDropdownClickUIEvent={sendMigrationPhaseDropdownClickUIEvent}
				sendMigrationPhaseDropdownItemClickUIEvent={sendMigrationPhaseDropdownItemClickUIEvent}
			/>

			{hasActiveFilters && (
				<Button
					appearance="subtle"
					onClick={() => {
						handleClearFilters();
						sendClearFiltersButtonClickUIEvent();
					}}
					testId="clear-filters-button"
				>
					<FormattedMessage {...messages.migrationTicketsFiltersClearFilters} />
				</Button>
			)}
		</>
	);
};
